import { useState } from 'react';
import { MapPin, Phone, Mail, Instagram, Facebook, Send, Clock } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Mesajınız göndərildi! Tezliklə sizinlə əlaqə saxlayacağıq.');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-brand-accent/30 via-brand-cream to-brand-secondary/30 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10">
          <span className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm text-brand-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Mail className="w-4 h-4" />
            Əlaqə
          </span>
          <h1 className="font-serif text-4xl md:text-6xl text-brand-brown">
            Bizimlə <span className="text-brand-primary italic">əlaqə</span> saxlayın
          </h1>
          <p className="mt-4 text-brand-muted max-w-xl mx-auto text-lg">
            Suallarınız var? Sifariş vermək istəyirsiniz? Bizimlə əlaqə saxlayın!
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-serif text-3xl text-brand-brown mb-8">Əlaqə məlumatları</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-card" data-testid="contact-phone">
                  <div className="w-14 h-14 rounded-2xl bg-brand-accent/30 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-brand-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-brand-brown mb-1">Telefon</h4>
                    <p className="text-brand-muted">+994 50 123 45 67</p>
                    <p className="text-brand-muted">+994 12 345 67 89</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-card" data-testid="contact-email">
                  <div className="w-14 h-14 rounded-2xl bg-brand-accent/30 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-brand-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-brand-brown mb-1">E-poçt</h4>
                    <p className="text-brand-muted">info@turkanrustamova.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-card" data-testid="contact-address">
                  <div className="w-14 h-14 rounded-2xl bg-brand-accent/30 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-brand-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-brand-brown mb-1">Ünvan</h4>
                    <p className="text-brand-muted">Bakı şəhəri, Azərbaycan</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-card" data-testid="contact-hours">
                  <div className="w-14 h-14 rounded-2xl bg-brand-accent/30 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-brand-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-brand-brown mb-1">İş saatları</h4>
                    <p className="text-brand-muted">Bazar ertəsi - Cümə: 10:00 - 19:00</p>
                    <p className="text-brand-muted">Şənbə: 11:00 - 17:00</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-8">
                <h4 className="font-medium text-brand-brown mb-4">Bizi izləyin</h4>
                <div className="flex gap-4">
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-primary to-[#E08E73] flex items-center justify-center text-white hover:shadow-warm hover:scale-105 transition-all duration-300"
                    data-testid="contact-instagram"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-primary to-[#E08E73] flex items-center justify-center text-white hover:shadow-warm hover:scale-105 transition-all duration-300"
                    data-testid="contact-facebook"
                  >
                    <Facebook className="w-6 h-6" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-warm">
              <h2 className="font-serif text-3xl text-brand-brown mb-2">Mesaj göndərin</h2>
              <p className="text-brand-muted mb-8">Formu doldurun, sizinlə əlaqə saxlayaq</p>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-2">
                    Adınız *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="rounded-xl border-brand-secondary bg-brand-cream/30 focus:border-brand-primary focus:ring-brand-primary/20"
                    placeholder="Ad və soyadınızı daxil edin"
                    data-testid="contact-input-name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-2">
                      E-poçt *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="rounded-xl border-brand-secondary bg-brand-cream/30 focus:border-brand-primary focus:ring-brand-primary/20"
                      placeholder="E-poçt ünvanınız"
                      data-testid="contact-input-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-2">
                      Telefon
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="rounded-xl border-brand-secondary bg-brand-cream/30 focus:border-brand-primary focus:ring-brand-primary/20"
                      placeholder="+994 XX XXX XX XX"
                      data-testid="contact-input-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-2">
                    Mesajınız *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="rounded-xl border-brand-secondary bg-brand-cream/30 focus:border-brand-primary focus:ring-brand-primary/20 resize-none"
                    placeholder="Mesajınızı yazın..."
                    data-testid="contact-input-message"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-primary to-[#E08E73] text-white px-8 py-4 font-medium hover:shadow-warm-lg hover:scale-[1.02] transition-all duration-300"
                  data-testid="contact-submit-btn"
                >
                  Göndər
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
